import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  CloseButton,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { SolidActionButton } from '@components/Buttons'
import { LabResult, SaveLabResultArgs, WithId } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { LogLabResultForm } from '../../../../components/Users/Profile/Labs/LogLabResultForm'
import { useNeonatalLabResults } from '../../../../hooks/backend'
import { LabResultRow } from '../LabQuickview/LabResultRow'

type SortMode = 'date-desc' | 'date-asc' | 'lab-type' | 'flag-status'

const sortLabel: Record<SortMode, string> = {
  'date-desc': 'Date (Newest)',
  'date-asc': 'Date (Oldest)',
  'lab-type': 'Lab Type',
  'flag-status': 'Flag Status',
}

const sortResults = (results: WithId<LabResult>[], mode: SortMode): WithId<LabResult>[] => {
  const copy = [...results]
  const dateOf = (r: LabResult) => r.resultDate ?? r.collectedDate ?? ''
  switch (mode) {
    case 'date-desc':
      return copy.sort((a, b) => dateOf(b).localeCompare(dateOf(a)))
    case 'date-asc':
      return copy.sort((a, b) => dateOf(a).localeCompare(dateOf(b)))
    case 'lab-type':
      return copy.sort((a, b) => a.labType.localeCompare(b.labType))
    case 'flag-status': {
      const order: Record<string, number> = { critical: 0, high: 1, low: 1, abnormal: 1, normal: 2 }
      return copy.sort((a, b) => (order[a.flagStatus] ?? 2) - (order[b.flagStatus] ?? 2))
    }
  }
}

interface Props {
  assessmentId: string
  patientId: string
  isOpen: boolean
  onClose: () => void
  onSave: (args: Partial<SaveLabResultArgs> & { labResultId: string }) => Promise<void>
}

export const NeonatalLabQuickview = ({
  assessmentId,
  patientId,
  isOpen,
  onClose,
  onSave,
}: Props) => {
  const { dataArr, loading } = useNeonatalLabResults(patientId, assessmentId)
  const [sortMode, setSortMode] = useState<SortMode>('date-desc')
  const [isLogging, setIsLogging] = useState(false)

  const results = useMemo(() => dataArr ?? [], [dataArr])
  const sorted = useMemo(() => sortResults(results, sortMode), [results, sortMode])

  const pendingCount = useMemo(
    () => results.filter(r => r.flagStatus !== 'normal' && !r.acknowledgedAt).length,
    [results],
  )

  const openLog = useCallback(() => setIsLogging(true), [])
  const closeLog = useCallback(() => setIsLogging(false), [])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius={8} h="85vh" display="flex" flexDir="column">
        <ModalHeader
          fontFamily="Hero-New"
          bg="teal.50"
          _dark={{ bg: 'teal.900', borderColor: 'teal.700' }}
          borderTopRadius={8}
          borderBottom="1px solid"
          borderColor="teal.200"
          py={2}
          px={4}
          fontSize="md">
          <Flex align="center" gap={3} flexWrap="wrap">
            <Text fontFamily="Hero-New" fontWeight={500} fontSize="lg">
              {isLogging ? 'Log Neonatal Lab Result' : 'Neonatal Lab Results'}
            </Text>
            {!isLogging && pendingCount > 0 && (
              <Badge colorScheme="orange" fontFamily="Hero-New" fontSize="xs">
                {pendingCount} pending acknowledgment
              </Badge>
            )}
            <Flex gap={2} ml="auto" align="center">
              {!isLogging && (
                <SolidActionButton
                  size="xs"
                  fontFamily="Hero-New"
                  colorScheme="teal"
                  onClick={openLog}>
                  + Log Result
                </SolidActionButton>
              )}
              <CloseButton onClick={isLogging ? closeLog : onClose} />
            </Flex>
          </Flex>
        </ModalHeader>

        {isLogging ? (
          <ModalBody p={0} flex={1} overflowY="auto">
            <LogLabResultForm
              assessmentId={assessmentId}
              patientId={patientId}
              recordType="neonatal"
              onSuccess={closeLog}
              onCancel={closeLog}
            />
          </ModalBody>
        ) : (
          <>
            <Flex
              px={4}
              py={2}
              borderBottom="1px solid"
              borderColor="gray.100"
              bg="gray.50"
              align="center"
              gap={2}
              flexShrink={0}>
              <Text fontSize="xs" color="gray.500" fontFamily="Hero-New" fontWeight={600}>
                Sort:
              </Text>
              <ButtonGroup size="xs" isAttached variant="outline">
                {(Object.keys(sortLabel) as SortMode[]).map((mode, i) => (
                  <Button
                    key={mode}
                    colorScheme={sortMode === mode ? 'teal' : 'gray'}
                    fontFamily="Hero-New"
                    borderLeftWidth={i === 0 || sortMode === mode ? 1 : 0}
                    onClick={() => setSortMode(mode)}>
                    {sortLabel[mode]}
                  </Button>
                ))}
              </ButtonGroup>
            </Flex>

            <ModalBody flex={1} overflowY="auto" py={3} px={4}>
              {loading ? (
                <Flex justify="center" align="center" py={12}>
                  <Spinner color="teal.400" />
                </Flex>
              ) : sorted.length === 0 ? (
                <Box py={10} textAlign="center">
                  <Text color="gray.400" fontFamily="Hero-New" fontSize="sm">
                    No neonatal lab results recorded yet.
                  </Text>
                  <SolidActionButton
                    mt={3}
                    size="sm"
                    fontFamily="Hero-New"
                    colorScheme="teal"
                    onClick={openLog}>
                    + Log First Result
                  </SolidActionButton>
                </Box>
              ) : (
                <Flex direction="column" gap={2}>
                  {sorted.map(r => (
                    <LabResultRow key={r.id} result={r} onSave={onSave} />
                  ))}
                </Flex>
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
